import { useState, useMemo, useCallback } from 'react';
import { useNoIndex } from '@/hooks/useSEO';
import { AdminStatsBar } from './components/AdminStatsBar';
import { AdminFilters } from './components/AdminFilters';
import { AdminOrderRow } from './components/AdminOrderRow';
import { AdminOrder, AdminStats } from './types';

const SUPABASE_FUNCTIONS_URL = 'https://opocfbvkglexckmxhwsv.supabase.co/functions/v1';
const ADMIN_KEY = 'zpiritualz-admin-2026';

const DEFAULT_STATS: AdminStats = {
  total: 0,
  paid: 0,
  unpaid: 0,
  blueprints: 0,
  consultations: 0,
  blueprintsSent: 0,
  blueprintsPending: 0,
  totalRevenue: 0,
};

// ── CSV Export Helper ──
const escapeCsvCell = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const formatPriceRaw = (cents: number | null, currency: string): string => {
  if (cents === null) return '';
  return (cents / 100).toFixed(2).replace('.', ',');
};

const formatDateRaw = (ts: number): string =>
  new Date(ts * 1000).toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const exportToCSV = (orders: AdminOrder[]) => {
  const headers = [
    'Bestelnummer',
    'Datum',
    'Naam',
    'E-mail',
    'Product',
    'Type',
    'Betaalstatus',
    'Blueprint verstuurd',
    'Bedrag',
    'Valuta',
    'Stripe ID',
    'Bon URL',
  ];

  const rows = orders.map((o) => {
    const productLabel =
      o.productName ??
      (o.type === 'consultation' && o.metadata?.consultation_type
        ? `Consult – ${o.metadata.consultation_type}`
        : o.type === 'blueprint'
        ? 'Ritueel Blueprint'
        : 'Aankoop');

    const blueprintStatus =
      o.blueprintSent === null ? '' : o.blueprintSent ? 'Ja' : 'Nee';

    return [
      escapeCsvCell(o.shortId),
      escapeCsvCell(formatDateRaw(o.created)),
      escapeCsvCell(o.customerName),
      escapeCsvCell(o.email),
      escapeCsvCell(productLabel),
      escapeCsvCell(
        o.type === 'blueprint'
          ? 'Blueprint'
          : o.type === 'consultation'
          ? 'Consult'
          : 'Aankoop'
      ),
      escapeCsvCell(o.payment_status === 'paid' ? 'Betaald' : o.payment_status),
      escapeCsvCell(blueprintStatus),
      escapeCsvCell(formatPriceRaw(o.amount_total, o.currency)),
      escapeCsvCell(o.currency.toUpperCase()),
      escapeCsvCell(o.id),
      escapeCsvCell(o.receiptUrl),
    ].join(',');
  });

  const csvContent = [headers.join(','), ...rows].join('\n');
  const bom = '\uFEFF'; // UTF-8 BOM for Excel compatibility
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const dateStr = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.download = `zpiritualz-bestellingen-${dateStr}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const AdminPage = () => {
  useNoIndex();

  // Auth gate
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  // Data
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [stats, setStats] = useState<AdminStats>(DEFAULT_STATS);
  const [loadStatus, setLoadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [exportFlash, setExportFlash] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState('');
  const [activeStatus, setActiveStatus] = useState('');

  const handleAuth = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_KEY) {
      setAuthed(true);
      setAuthError('');
      fetchOrders();
    } else {
      setAuthError('Onjuist wachtwoord. Probeer opnieuw.');
    }
  };

  const fetchOrders = useCallback(async () => {
    setLoadStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/get-admin-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY,
        },
        body: JSON.stringify({ limit: 200 }),
      });
      const data = await res.json();
      if (data.error) {
        setErrorMsg(data.error);
        setLoadStatus('error');
        return;
      }
      setOrders(data.orders ?? []);
      setStats(data.stats ?? DEFAULT_STATS);
      setLoadStatus('success');
      setLastRefresh(new Date());
    } catch {
      setErrorMsg('Verbinding mislukt. Controleer je internetverbinding.');
      setLoadStatus('error');
    }
  }, []);

  // Client-side filtering
  const filtered = useMemo(() => {
    let result = orders;

    if (activeType) {
      result = result.filter((o) => o.type === activeType);
    }

    if (activeStatus === 'paid') {
      result = result.filter((o) => o.payment_status === 'paid');
    } else if (activeStatus === 'unpaid') {
      result = result.filter((o) => o.payment_status !== 'paid');
    } else if (activeStatus === 'blueprint_pending') {
      result = result.filter((o) => o.blueprintSent === false);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (o) =>
          o.email?.toLowerCase().includes(q) ||
          o.customerName?.toLowerCase().includes(q) ||
          o.productName?.toLowerCase().includes(q) ||
          o.shortId.toLowerCase().includes(q) ||
          o.id.toLowerCase().includes(q)
      );
    }

    return result;
  }, [orders, activeType, activeStatus, search]);

  const handleExport = () => {
    exportToCSV(filtered);
    setExportFlash(true);
    setTimeout(() => setExportFlash(false), 2000);
  };

  // ── Auth Gate ──
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050010] flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-violet-600/20 border border-violet-500/25 mx-auto mb-4">
              <i className="ri-shield-keyhole-line text-3xl text-violet-400" />
            </div>
            <h1 className="text-white font-black text-2xl mb-1">Admin Dashboard</h1>
            <p className="text-white/35 text-sm">ZpiritualZ — Bestellingen Overzicht</p>
          </div>

          <form
            onSubmit={handleAuth}
            className="bg-[#110020]/80 border border-violet-500/20 rounded-2xl p-6"
          >
            <label className="block text-white/60 text-xs font-medium mb-2">Wachtwoord</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              autoFocus
              className="w-full px-4 py-3 bg-white/5 border border-violet-500/20 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-colors mb-3"
            />
            {authError && (
              <p className="text-rose-400 text-xs mb-3 flex items-center gap-1.5">
                <i className="ri-error-warning-line" />
                {authError}
              </p>
            )}
            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold text-sm rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
            >
              Inloggen
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard ──
  return (
    <div className="min-h-screen bg-[#050010] pt-6 pb-16">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pt-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-violet-600/20 border border-violet-500/25">
                <i className="ri-dashboard-3-line text-violet-400 text-base" />
              </div>
              <h1 className="text-white font-black text-2xl">Admin Dashboard</h1>
            </div>
            <p className="text-white/30 text-sm ml-12">
              ZpiritualZ — Bestellingen &amp; Blueprint Status
              {lastRefresh && (
                <span className="ml-2 text-white/20">
                  · Bijgewerkt {lastRefresh.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={fetchOrders}
              disabled={loadStatus === 'loading'}
              className="flex items-center gap-2 px-4 py-2.5 bg-violet-600/15 border border-violet-500/25 text-violet-400 text-sm font-medium rounded-xl hover:bg-violet-600/25 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
            >
              <i className={`ri-refresh-line text-sm ${loadStatus === 'loading' ? 'animate-spin' : ''}`} />
              Vernieuwen
            </button>

            {loadStatus === 'success' && (
              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2.5 border text-sm font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                  exportFlash
                    ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                    : 'bg-white/5 border-white/10 text-white/60 hover:border-emerald-500/30 hover:text-emerald-400 hover:bg-emerald-500/10'
                }`}
              >
                <i className={`text-sm ${exportFlash ? 'ri-check-line' : 'ri-download-2-line'}`} />
                {exportFlash
                  ? 'Gedownload!'
                  : `CSV exporteren${filtered.length !== orders.length ? ` (${filtered.length})` : ` (${orders.length})`}`}
              </button>
            )}

            <a
              href="https://dashboard.stripe.com/payments"
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 text-white/60 text-sm font-medium rounded-xl hover:border-violet-500/30 hover:text-violet-400 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line text-sm" />
              Stripe Dashboard
            </a>
          </div>
        </div>

        {/* Loading */}
        {loadStatus === 'loading' && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-violet-600/15 border border-violet-500/20">
              <i className="ri-loader-4-line animate-spin text-2xl text-violet-400" />
            </div>
            <p className="text-white/40 text-sm">Bestellingen ophalen uit Stripe...</p>
          </div>
        )}

        {/* Error */}
        {loadStatus === 'error' && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-rose-600/15 border border-rose-500/20">
              <i className="ri-error-warning-line text-2xl text-rose-400" />
            </div>
            <p className="text-rose-400 text-sm">{errorMsg}</p>
            <button
              onClick={fetchOrders}
              className="flex items-center gap-2 px-5 py-2.5 bg-violet-600/15 border border-violet-500/25 text-violet-400 text-sm rounded-xl hover:bg-violet-600/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" />
              Opnieuw proberen
            </button>
          </div>
        )}

        {/* Success */}
        {loadStatus === 'success' && (
          <>
            {/* Stats */}
            <AdminStatsBar stats={stats} />

            {/* Blueprint alert */}
            {stats.blueprintsPending > 0 && (
              <div className="flex items-start gap-3 p-4 bg-rose-500/10 border border-rose-500/25 rounded-2xl mb-5">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-500/20 shrink-0">
                  <i className="ri-alarm-warning-line text-rose-400 text-base" />
                </div>
                <div>
                  <p className="text-rose-400 font-semibold text-sm mb-0.5">
                    {stats.blueprintsPending} blueprint{stats.blueprintsPending !== 1 ? 's' : ''} nog niet verstuurd
                  </p>
                  <p className="text-rose-400/60 text-xs">
                    Gebruik het filter &quot;Blueprint uitstaand&quot; om deze bestellingen te bekijken en handmatig op te volgen.
                  </p>
                </div>
                <button
                  onClick={() => setActiveStatus('blueprint_pending')}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-rose-500/20 border border-rose-500/30 text-rose-400 text-xs font-medium rounded-lg hover:bg-rose-500/30 transition-all cursor-pointer whitespace-nowrap shrink-0"
                >
                  <i className="ri-filter-line text-xs" />
                  Toon uitstaand
                </button>
              </div>
            )}

            {/* Filters */}
            <AdminFilters
              activeType={activeType}
              activeStatus={activeStatus}
              search={search}
              onTypeChange={setActiveType}
              onStatusChange={setActiveStatus}
              onSearchChange={setSearch}
              total={filtered.length}
            />

            {/* Orders list */}
            {filtered.length === 0 ? (
              <div className="text-center py-16 bg-[#110020]/40 border border-violet-500/10 rounded-2xl">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-violet-600/10 border border-violet-500/15 mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-violet-400/40" />
                </div>
                <p className="text-white/40 text-sm">Geen bestellingen gevonden voor deze filters.</p>
                <button
                  onClick={() => { setSearch(''); setActiveType(''); setActiveStatus(''); }}
                  className="mt-4 text-violet-400 text-xs hover:text-violet-300 transition-colors cursor-pointer"
                >
                  Filters wissen
                </button>
              </div>
            ) : (
              <div className="space-y-2.5">
                {filtered.map((order) => (
                  <AdminOrderRow key={order.id} order={order} />
                ))}
              </div>
            )}

            {/* Footer note */}
            <div className="mt-8 flex items-center justify-center gap-2 text-white/20 text-xs">
              <i className="ri-lock-line text-xs" />
              Beveiligd admin-paneel — niet geïndexeerd door zoekmachines
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
